import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { NotifyInstoreCartOpenedDocument } from 'src/gql/graphql';

export function useNotifyInstoreCartOpened(
  cartToken: string | null,
  dispensaryId: string | null,
  orderId: string | null,
  isKioskOrHubFlow: boolean,
  isLoggedIn: boolean
): void {
  const flags = useFlags();
  const mobileCheckoutPbbEnabled = Boolean(flags['fintech.retail-payments.mobile-checkout-pbb']);
  const hasFired = useRef(false);
  const [notifyInstoreCartOpened] = useMutation(NotifyInstoreCartOpenedDocument);

  useEffect(() => {
    // Gated on the mobile-checkout PBB flag, and excludes kiosk/hub in-store flows
    // (which also carry orderId) so only mobile checkout triggers the notify. Also
    // waits for isLoggedIn so an unauthenticated first render doesn't fire (and 401)
    // before the user completes login - hasFired only latches on success below, so
    // a failed attempt can still retry once a dependency (e.g. isLoggedIn) changes.
    if (!mobileCheckoutPbbEnabled || isKioskOrHubFlow || !cartToken || !isLoggedIn || hasFired.current) {
      return;
    }

    // Armageddon requires dispensary_id or order_id to resolve the cart.
    if (!dispensaryId && !orderId) {
      return;
    }

    void notifyInstoreCartOpened({
      variables: {
        instoreCart: {
          cartToken,
          ...(dispensaryId ? { dispensaryId } : {}),
          ...(orderId ? { orderId } : {}),
        },
      },
    })
      .then(() => {
        hasFired.current = true;
      })
      .catch(() => undefined);
  }, [
    mobileCheckoutPbbEnabled,
    isKioskOrHubFlow,
    cartToken,
    dispensaryId,
    orderId,
    isLoggedIn,
    notifyInstoreCartOpened,
  ]);
}
